/**
 * Web → CLI handoff for dispute-council resolve (multisig-tool).
 * Portal shows the case; council members sign + submit via local tool.
 */

import { predictionMarketContractId } from "./chain-config.js";
import contracts from "./contracts.testnet.json";

/** @returns {{ accountId: number|null, threshold: number|null, registryId: string|null }} */
export function disputeCouncilConfig() {
  const c = contracts.predictionMarket?.disputeCouncil;
  if (!c || typeof c !== "object") {
    return { accountId: null, threshold: null, registryId: null };
  }
  const accountId =
    c.accountId != null && Number.isFinite(Number(c.accountId))
      ? Number(c.accountId)
      : null;
  const threshold =
    c.threshold != null && Number.isFinite(Number(c.threshold))
      ? Number(c.threshold)
      : null;
  const registryId = c.registryContractId
    ? String(c.registryContractId).replace(/^0x/i, "")
    : null;
  return { accountId, threshold, registryId };
}

function pmHex() {
  const id = predictionMarketContractId() || "";
  const bare = String(id).replace(/^0x/i, "");
  return bare ? `0x${bare}` : "0x<PM_CONTRACT_ID>";
}

/**
 * Build copy-paste CLI steps for a market under review.
 * @param {{ marketId: number|string, pendingOutcome?: number|null }} opts
 */
export function councilCliSteps({ marketId, pendingOutcome = null }) {
  const { accountId, threshold } = disputeCouncilConfig();
  const acct = accountId != null ? String(accountId) : "<REGISTRY_ACCOUNT_ID>";
  const thr = threshold != null ? String(threshold) : "2";
  const mid = String(marketId);
  const pm = pmHex();
  // Hint which outcome the pending side claimed; council still chooses YES(0)/NO(1).
  const outcomeHint =
    pendingOutcome === 0
      ? "0  # YES - or 1 for NO"
      : pendingOutcome === 1
        ? "1  # NO - or 0 for YES"
        : "0|1  # 0=YES, 1=NO";

  return [
    {
      title: "Read the case here",
      body: "This page is the dispute brief (status, bond, evidence). Signing happens in multisig-tool on your machine - keys never enter the browser.",
    },
    {
      title: "Open the council UI (local browser)",
      code: `cd multisig
export MULTISIG_TOOL_PWD=…          # identity store
export RUSK_WALLET_PWD=…            # gas payer (submit only)
export MULTISIG_COLLECTOR_URL=http://127.0.0.1:8899   # start collector first

# Standalone UI (not the Multisig Lab treasury demo):
cargo run -p multisig-tool -- pm-resolve ui \\
  --market ${mid} --outcome ${pendingOutcome === 1 ? 1 : 0} \\
  --pm ${pm} --account ${acct} --threshold ${thr}`,
    },
    {
      title: "Or CLI-only: init → sign → submit",
      code: `# Pick winning outcome: ${outcomeHint}
cargo run -p multisig-tool -- pm-resolve init \\
  --market ${mid} --outcome ${pendingOutcome === 1 ? 1 : 0} \\
  --pm ${pm} --account ${acct} --threshold ${thr} \\
  --summary 'market ${mid} under_review' --out /tmp/pm-resolve-${mid}.json

# Each council member (own machine / own --as name):
cargo run -p multisig-tool -- pm-resolve sign \\
  --file /tmp/pm-resolve-${mid}.json --as alice
# With collector: add --id <digest> and MULTISIG_COLLECTOR_URL=…

# Anyone with enough partials submits (pays gas):
cargo run -p multisig-tool -- pm-resolve status --file /tmp/pm-resolve-${mid}.json
cargo run -p multisig-tool -- pm-resolve submit --file /tmp/pm-resolve-${mid}.json`,
    },
    {
      title: "After submit",
      body: "Refresh this page - status should become Finalized. Traders claim from Portfolio. Owner portal Resolve buttons are an escape hatch only; ops should use the council path.",
    },
  ];
}

/**
 * DOM callout: council action needed + CLI handoff.
 * @param {{ marketId: number|string, pendingOutcome?: number|null }} opts
 */
export function renderCouncilHandoff({ marketId, pendingOutcome = null }) {
  const root = document.createElement("div");
  root.className = "council-handoff";
  root.setAttribute("role", "region");
  root.setAttribute("aria-label", "Council resolve handoff");

  const banner = document.createElement("div");
  banner.className = "council-handoff-banner";
  const title = document.createElement("strong");
  title.textContent = "Council action needed";
  const sub = document.createElement("p");
  sub.textContent =
    "This event is under review. Dispute-council members resolve it with the local **PM council resolve** UI (`multisig-tool pm-resolve ui`) - not by clicking Resolve in this portal.";
  banner.append(title, sub);
  root.append(banner);

  const { accountId } = disputeCouncilConfig();
  if (accountId == null) {
    const warn = document.createElement("p");
    warn.className = "council-handoff-warn";
    warn.textContent =
      "Portal config has no disputeCouncil.accountId yet - fill contracts.testnet.json after OPS wire (or replace <REGISTRY_ACCOUNT_ID> in the commands).";
    root.append(warn);
  }

  const details = document.createElement("details");
  details.className = "council-handoff-details";
  const summary = document.createElement("summary");
  summary.textContent = "How to resolve (local UI or CLI)";
  details.append(summary);

  const steps = councilCliSteps({ marketId, pendingOutcome });
  const list = document.createElement("ol");
  list.className = "council-handoff-steps";
  for (const step of steps) {
    const li = document.createElement("li");
    const h = document.createElement("strong");
    h.textContent = step.title;
    li.append(h);
    if (step.body) {
      const p = document.createElement("p");
      p.textContent = step.body;
      li.append(p);
    }
    if (step.code) {
      const pre = document.createElement("pre");
      pre.className = "council-handoff-code";
      pre.textContent = step.code;
      const copy = document.createElement("button");
      copy.type = "button";
      copy.className = "council-handoff-copy";
      copy.textContent = "Copy";
      copy.addEventListener("click", async () => {
        try {
          await navigator.clipboard.writeText(step.code);
          copy.textContent = "Copied";
          setTimeout(() => {
            copy.textContent = "Copy";
          }, 1500);
        } catch {
          copy.textContent = "Select text";
        }
      });
      const block = document.createElement("div");
      block.className = "council-handoff-code-wrap";
      block.append(copy, pre);
      li.append(block);
    }
    list.append(li);
  }
  details.append(list);
  root.append(details);
  return root;
}
